import { c as _c } from "react/compiler-runtime";
import type { InitialTableState } from '@tanstack/react-table';
import { experimental_useObject as useObject } from '@ai-sdk/react';
import usePagination from '@mui/material/usePagination';
import { createColumnHelper, getCoreRowModel, getExpandedRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import clsx from 'clsx';
import { identity } from 'es-toolkit';
import { useSessionStorage } from 'foxact/use-session-storage';
import { atom, useAtomValue, useStore } from 'jotai';
import { useImmerAtom } from 'jotai-immer';
import { atomWithStorage } from 'jotai/utils';
import { startTransition, useDeferredValue, useRef } from 'react';
import { toast } from 'sonner';
import type { LearningPhase, Sentence } from '@/app/[locale]/(vocabulary)/_lib/LexiconTrie';
import type { VocabularySourceData, VocabularySourceState } from '@/app/[locale]/(vocabulary)/_lib/vocab';
import type { ColumnFilterFn } from '@/lib/table-utils';
import type { PartialObjectZ } from '@/types/utils';
import { isSourceTextStaleAtom } from '@/app/[locale]/(vocabulary)/_atoms';
import { AcquaintAllDialog } from '@/app/[locale]/(vocabulary)/_components/acquaint-all-dialog';
import { useVocabularyCommonColumns } from '@/app/[locale]/(vocabulary)/_components/columns';
import { ExampleSentence } from '@/app/[locale]/(vocabulary)/_components/example-sentence';
import { VocabularyMenu } from '@/app/[locale]/(vocabulary)/_components/menu';
import { VocabStatics } from '@/app/[locale]/(vocabulary)/_components/statics-bar';
import { searchFilterValue } from '@/app/[locale]/(vocabulary)/_lib/filters';
import { LEARNING_PHASE } from '@/app/[locale]/(vocabulary)/_lib/LexiconTrie';
import { useManagedVocabulary } from '@/app/[locale]/(vocabulary)/_lib/vocab-utils';
import { getCategory } from '@/app/[locale]/(vocabulary)/_prompts/getCategory';
import { categoriesSchema } from '@/app/api/categories/schema';
import { DataTableFacetedFilter } from '@/components/data-table/data-table-faceted-filter';
import { TableGoToLastPage } from '@/components/my-table/go-to-last-page';
import { TablePagination } from '@/components/my-table/pagination';
import { TablePaginationSizeSelect } from '@/components/my-table/pagination-size-select';
import { SearchWidget } from '@/components/search-widget';
import { Button } from '@/components/ui/button';
import { DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Div } from '@/components/ui/html-elements';
import { SegmentedControl, SegmentItem } from '@/components/ui/segmented-control';
import { Spinner } from '@/components/ui/spinner';
import { HeaderTitle, TableDataCell, TableHeader, TableHeaderCell, TableHeaderCellRender, TableRow } from '@/components/ui/table-element';
import { combineFilters, filterFn, noFilter } from '@/lib/table-utils';
import { cn } from '@/lib/utils';
import { useI18n } from '@/locales/client';
import { useClone, useLastTruthy } from '@sub-vocab/utils/hooks';
import { findClosest, isRegexValid } from '@sub-vocab/utils/lib';
import { narrow } from '@sub-vocab/utils/types';
type TableData = VocabularySourceState & {
  category: string | null;
};

/// keep-unique
const PAGE_SIZES = [10, 20, 40, 50, 100, 200, 500, 1000] as const;
const cacheStateAtom = atom({
  isUsingRegex: false,
  searchValue: '',
  filterValue: identity<Record<string, boolean>>({})
});
const initialTableStateAtom = atom(identity<InitialTableState>({
  columnOrder: ['frequency', 'word', 'word.length', 'acquaintedStatus', 'rank'],
  pagination: {
    pageSize: findClosest(100, PAGE_SIZES),
    pageIndex: 0
  }
}));
function useSegments() {
  const $ = _c(2);
  const t = useI18n();
  let t0;
  if ($[0] !== t) {
    t0 = narrow([{
      value: "all",
      label: t("all")
    }, {
      value: "new",
      label: t("new")
    }, {
      value: "acquainted",
      label: t("acquainted")
    }]);
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
type Segment = ReturnType<typeof useSegments>[number]['value'];
const SEGMENT_NAME = 'source-table-segment';
function acquaintedStatusFilter(filterSegment: Segment): ColumnFilterFn<TableData> {
  let filteredValue: LearningPhase[] = [];
  if (filterSegment === 'new') {
    filteredValue = [LEARNING_PHASE.NEW, LEARNING_PHASE.RETAINING];
  } else if (filterSegment === 'acquainted') {
    filteredValue = [LEARNING_PHASE.ACQUAINTED, LEARNING_PHASE.FADING];
  } else {
    return noFilter;
  }
  return row => filteredValue.includes(row.inertialPhase);
}
function categoryFilter(filterValue: Record<string, boolean>): ColumnFilterFn<TableData> {
  const categories = Object.entries(filterValue).filter(([, v]) => v).map(([k]) => k);
  if (categories.length === 0) {
    return noFilter;
  }
  return row => categories.includes(row.category || 'others');
}
function useSourceColumns(rootRef) {
  const $ = _c(5);
  const t = useI18n();
  let t0;
  if ($[0] !== rootRef?.current || $[1] !== t) {
    const columnHelper = createColumnHelper();
    t0 = columnHelper.accessor(_temp, {
      id: "frequency",
      filterFn,
      header: t1 => {
        const {
          header
        } = t1;
        const isSorted = header.column.getIsSorted();
        const title = t("frequency");
        return <TableHeaderCell header={header} className="w-[.1%] active:bg-background-active"><Div className="min-w-18 grow gap-0.5 pr-1 pl-2 select-none" onClick={e => {
            header.column.getToggleSortingHandler()?.(e);
            requestAnimationFrame(() => rootRef?.current?.scrollTo({
              top: 0
            }));
          }}><HeaderTitle title={title} isSorted={isSorted} className="*:data-title:text-right" /></Div></TableHeaderCell>;
      },
      cell: _temp2
    });
    $[0] = rootRef?.current;
    $[1] = t;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== t0) {
    t1 = [t0];
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function _temp2(t0) {
  const {
    row: row_0,
    cell,
    getValue,
    onExpandedChange
  } = t0;
  const value = getValue();
  const isExpanded = row_0.getIsExpanded();
  return <TableDataCell cell={cell}><Div className="pr-px pl-0.5 text-zinc-400">{row_0.getCanExpand() ? <button type="button" className="flex h-full grow items-center justify-between gap-1 px-3" onClick={() => {
        onExpandedChange?.(isExpanded);
      }}><svg className={clsx("icon-[lucide--chevron-right] size-3.5 text-zinc-400 transition-transform duration-200 dark:text-zinc-500", isExpanded ? "rotate-90" : "")} /><span className="float-right inline-block tracking-[.03em] tabular-nums">{value}</span></button> : <div className="w-full justify-end px-3"><span className="float-right inline-block tracking-[.03em] tabular-nums">{value}</span></div>}</Div></TableDataCell>;
}
function _temp(row) {
  return row.locators.length;
}
type CategoryValue = 'properName' | 'acronym';
type VocabularyCategory = { [K in CategoryValue]?: string[] };
const categoryAtom = atomWithStorage<VocabularyCategory>('categoryAtom', {}, undefined, {
  getOnInit: true
});
categoryAtom.debugLabel = 'categoryAtom';
function useCategorize(vocabularyCategory, data) {
  const $ = _c(11);
  useI18n();
  const {
    properName: t0,
    acronym: t1
  } = vocabularyCategory;
  let t2;
  if ($[0] !== t0) {
    t2 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const properName = t2;
  let t3;
  if ($[2] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[2] = t1;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const acronym = t3;
  let t4;
  if ($[4] !== acronym || $[5] !== data || $[6] !== properName) {
    let t5;
    if ($[8] !== acronym || $[9] !== properName) {
      t5 = d => {
        let category = null;
        if (d.wordFamily.some(w_0 => properName.includes(w_0.pathe))) {
          category = "properName";
        } else {
          if (d.wordFamily.some(w => acronym.includes(w.pathe))) {
            category = "acronym";
          }
        }
        return {
          ...d,
          category
        };
      };
      $[8] = acronym;
      $[9] = properName;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t4 = data.map(t5);
    $[4] = acronym;
    $[5] = data;
    $[6] = properName;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
export function VocabSourceTable(t0) {
  const $ = _c(140);
  const {
    data: rows,
    sentences,
    className: t1,
    onSentenceTrack
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const [data, handlePurge] = useManagedVocabulary(rows);
  const store = useStore();
  const t = useI18n();
  const [t2, setCacheState] = useImmerAtom(cacheStateAtom);
  const {
    isUsingRegex,
    searchValue,
    filterValue
  } = t2;
  const deferredSearchValue = useDeferredValue(searchValue);
  const deferredIsUsingRegex = useDeferredValue(isUsingRegex);
  const rootRef = useRef(null);
  const vocabularyCommonColumns = useVocabularyCommonColumns(rootRef);
  const sourceColumns = useSourceColumns(rootRef);
  const columns = [...vocabularyCommonColumns, ...sourceColumns];
  const segments = useSegments();
  const [segment, setSegment] = useSessionStorage(`${SEGMENT_NAME}-value`, "all");
  const segmentDeferredValue = useDeferredValue(segment);
  const lastTruthySearchFilterValue = useLastTruthy(searchFilterValue(deferredSearchValue, deferredIsUsingRegex))() ?? noFilter;
  const inValidSearch = deferredIsUsingRegex && !isRegexValid(deferredSearchValue);
  const isSourceTextStale = useAtomValue(isSourceTextStaleAtom);
  let t3;
  let t4;
  if ($[0] !== store) {
    t3 = result => {
      if (result.object) {
        store.set(categoryAtom, result.object);
      }
    };
    t4 = store.get(categoryAtom);
    $[0] = store;
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t3 || $[4] !== t4) {
    t5 = {
      api: "/api/categories",
      schema: categoriesSchema,
      onError: _temp3,
      onFinish: t3,
      initialValue: t4
    };
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const {
    object: t6,
    submit,
    isLoading
  } = useObject(t5);
  let t7;
  if ($[6] !== t6) {
    t7 = t6 === undefined ? {} : t6;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const categoryValue = t7;
  const categorizedData = useCategorize(categoryValue, data);
  const preCategoryFilters = [acquaintedStatusFilter(segmentDeferredValue), lastTruthySearchFilterValue];
  const globalFilter = combineFilters(preCategoryFilters);
  let t8;
  if ($[8] !== sentences) {
    t8 = row_0 => sentences.length > 0 && row_0.original.locators.length > 0;
    $[8] = sentences;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const table = useClone(useReactTable({
    data: categorizedData,
    columns,
    state: {
      globalFilter,
      columnFilters: [{
        id: "word",
        value: categoryFilter(filterValue)
      }]
    },
    globalFilterFn: filterFn,
    initialState: store.get(initialTableStateAtom),
    autoResetPageIndex: false,
    getRowId: _temp4,
    getRowCanExpand: t8,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onStateChange: updater => {
      store.set(initialTableStateAtom, typeof updater === "function" ? updater(tableState) : updater);
    },
    getSortedRowModel: getSortedRowModel()
  }));
  const tableState = table.getState();
  let t9;
  if ($[10] !== table) {
    t9 = table.getPageCount();
    $[10] = table;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = tableState.pagination.pageIndex + 1;
  let t11;
  if ($[12] !== t10 || $[13] !== t9) {
    t11 = {
      count: t9,
      page: t10
    };
    $[12] = t10;
    $[13] = t9;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const {
    items
  } = usePagination(t11);
  const filteredRows = table.getFilteredRowModel().rows;
  const preFilteredRows = table.getPreFilteredRowModel().rows;
  const {
    rowsAcquainted: t12,
    rowsNew: t13
  } = Object.groupBy(filteredRows, _temp5);
  const rowsAcquainted = t12 === undefined ? [] : t12;
  const rowsNew = t13 === undefined ? [] : t13;
  const preCategoryFilteredRows = preFilteredRows.filter(row_2 => globalFilter(row_2.original));
  const {
    properName: t14,
    acronym: t15,
    others: t16
  } = Object.groupBy(preCategoryFilteredRows, _temp6);
  let t17;
  if ($[15] !== t14) {
    t17 = t14 === undefined ? [] : t14;
    $[15] = t14;
    $[16] = t17;
  } else {
    t17 = $[16];
  }
  const properName = t17;
  let t18;
  if ($[17] !== t15) {
    t18 = t15 === undefined ? [] : t15;
    $[17] = t15;
    $[18] = t18;
  } else {
    t18 = $[18];
  }
  const acronym = t18;
  let t19;
  if ($[19] !== t16) {
    t19 = t16 === undefined ? [] : t16;
    $[19] = t16;
    $[20] = t19;
  } else {
    t19 = $[20];
  }
  const others = t19;
  const rowsToRetain = rowsNew.map(_temp7);
  const isStale = isSourceTextStale || segment !== segmentDeferredValue || searchValue !== deferredSearchValue || isUsingRegex !== deferredIsUsingRegex;
  let t20;
  if ($[21] !== properName.length) {
    t20 = {
      label: "Name",
      value: "properName",
      count: properName.length
    };
    $[21] = properName.length;
    $[22] = t20;
  } else {
    t20 = $[22];
  }
  let t21;
  if ($[23] !== acronym.length) {
    t21 = {
      label: "Acronym",
      value: "acronym",
      count: acronym.length
    };
    $[23] = acronym.length;
    $[24] = t21;
  } else {
    t21 = $[24];
  }
  let t22;
  if ($[25] !== others.length) {
    t22 = {
      label: "Others",
      value: "others",
      count: others.length
    };
    $[25] = others.length;
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  let t23;
  if ($[27] !== t20 || $[28] !== t21 || $[29] !== t22) {
    t23 = [t20, t21, t22];
    $[27] = t20;
    $[28] = t21;
    $[29] = t22;
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  const options = t23;
  let t24;
  if ($[31] !== data) {
    t24 = data.filter(_temp8);
    $[31] = data;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  const freshVocabularies = t24;
  let t25;
  if ($[33] !== freshVocabularies || $[34] !== submit) {
    t25 = () => {
      const words = freshVocabularies.filter(_temp9).map(_temp11).flat();
      submit(getCategory(words));
    };
    $[33] = freshVocabularies;
    $[34] = submit;
    $[35] = t25;
  } else {
    t25 = $[35];
  }
  const handleAiVocabCategorize = t25;
  let t26;
  if ($[36] !== className) {
    t26 = cn("flex h-full flex-col items-center overflow-hidden bg-background will-change-transform", className);
    $[36] = className;
    $[37] = t26;
  } else {
    t26 = $[37];
  }
  let t27;
  if ($[38] !== t) {
    t27 = t("Options");
    $[38] = t;
    $[39] = t27;
  } else {
    t27 = $[39];
  }
  let t28;
  if ($[40] !== t27) {
    t28 = <DropdownMenuLabel>{t27}</DropdownMenuLabel>;
    $[40] = t27;
    $[41] = t28;
  } else {
    t28 = $[41];
  }
  let t29;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <DropdownMenuSeparator />;
    $[42] = t29;
  } else {
    t29 = $[42];
  }
  let t30;
  if ($[43] !== rowsToRetain) {
    t30 = <DropdownMenuGroup><DropdownMenuItem onSelect={_temp12} className="p-0"><AcquaintAllDialog vocabulary={rowsToRetain} /></DropdownMenuItem></DropdownMenuGroup>;
    $[43] = rowsToRetain;
    $[44] = t30;
  } else {
    t30 = $[44];
  }
  let t31;
  if ($[45] !== t28 || $[46] !== t30) {
    t31 = <VocabularyMenu>{t28}{t29}{t30}</VocabularyMenu>;
    $[45] = t28;
    $[46] = t30;
    $[47] = t31;
  } else {
    t31 = $[47];
  }
  let t32;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <div className="p-0.5" />;
    $[48] = t32;
  } else {
    t32 = $[48];
  }
  let t33;
  if ($[49] !== setCacheState) {
    t33 = v => {
      setCacheState(draft => {
        draft.filterValue = v;
      });
    };
    $[49] = setCacheState;
    $[50] = t33;
  } else {
    t33 = $[50];
  }
  let t34;
  if ($[51] !== filterValue || $[52] !== options || $[53] !== t33) {
    t34 = <DataTableFacetedFilter title="Category" className="[--sq-r:.875rem]" options={options} filterValue={filterValue} onFilterChange={t33} />;
    $[51] = filterValue;
    $[52] = options;
    $[53] = t33;
    $[54] = t34;
  } else {
    t34 = $[54];
  }
  let t35;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div className="p-0.5" />;
    $[55] = t35;
  } else {
    t35 = $[55];
  }
  const t36 = freshVocabularies.length === 0 || isLoading;
  let t37;
  if ($[56] !== isLoading) {
    t37 = isLoading ? <svg className="icon-[lucide--loader] size-4.5 animate-spin duration-1000 [animation-duration:2s]" /> : <svg className="icon-[iconoir--sparks] size-4.5" />;
    $[56] = isLoading;
    $[57] = t37;
  } else {
    t37 = $[57];
  }
  let t38;
  if ($[58] !== handleAiVocabCategorize || $[59] !== t36 || $[60] !== t37) {
    t38 = <Button className="aspect-square h-full p-0 [--sq-r:.8125rem]" variant="ghost" disabled={t36} onClick={handleAiVocabCategorize} aria-label="ai categorize">{t37}</Button>;
    $[58] = handleAiVocabCategorize;
    $[59] = t36;
    $[60] = t37;
    $[61] = t38;
  } else {
    t38 = $[61];
  }
  const t39 = isStale ? "" : "opacity-0";
  let t40;
  if ($[62] !== t39) {
    t40 = clsx("flex justify-center transition-opacity delay-300 duration-200", t39);
    $[62] = t39;
    $[63] = t40;
  } else {
    t40 = $[63];
  }
  let t41;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <Spinner variant="primary" size="sm" />;
    $[64] = t41;
  } else {
    t41 = $[64];
  }
  let t42;
  if ($[65] !== t40) {
    t42 = <div className="flex items-center px-2.5 text-base"><div className={t40}>{t41}</div></div>;
    $[65] = t40;
    $[66] = t42;
  } else {
    t42 = $[66];
  }
  let t43;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <div className="grow" />;
    $[67] = t43;
  } else {
    t43 = $[67];
  }
  let t44;
  let t45;
  if ($[68] !== setCacheState) {
    t44 = v_0 => {
      setCacheState(draft_0 => {
        draft_0.searchValue = v_0;
      });
    };
    t45 = v_1 => {
      setCacheState(draft_1 => {
        draft_1.isUsingRegex = v_1;
      });
    };
    $[68] = setCacheState;
    $[69] = t44;
    $[70] = t45;
  } else {
    t44 = $[69];
    t45 = $[70];
  }
  const t46 = inValidSearch ? "!border-red-500" : "";
  let t47;
  if ($[71] !== isUsingRegex || $[72] !== searchValue || $[73] !== t44 || $[74] !== t45 || $[75] !== t46) {
    t47 = <SearchWidget value={searchValue} isUsingRegex={isUsingRegex} onSearch={t44} onRegex={t45} className={t46} />;
    $[71] = isUsingRegex;
    $[72] = searchValue;
    $[73] = t44;
    $[74] = t45;
    $[75] = t46;
    $[76] = t47;
  } else {
    t47 = $[76];
  }
  let t48;
  if ($[77] !== t31 || $[78] !== t34 || $[79] !== t38 || $[80] !== t42 || $[81] !== t47) {
    t48 = <div className="z-10 flex h-12 w-full justify-between bg-background p-2">{t31}{t32}{t34}{t35}{t38}{t42}{t43}{t47}</div>;
    $[77] = t31;
    $[78] = t34;
    $[79] = t38;
    $[80] = t42;
    $[81] = t47;
    $[82] = t48;
  } else {
    t48 = $[82];
  }
  let t49;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <div className="h-px w-full border-b border-border" />;
    $[83] = t49;
  } else {
    t49 = $[83];
  }
  let t50;
  if ($[84] !== handlePurge || $[85] !== setSegment) {
    t50 = newSegment => {
      setSegment(newSegment);
      startTransition(() => {
        handlePurge();
      });
    };
    $[84] = handlePurge;
    $[85] = setSegment;
    $[86] = t50;
  } else {
    t50 = $[86];
  }
  let t51;
  if ($[87] !== segments) {
    t51 = segments.map(_temp13);
    $[87] = segments;
    $[88] = t51;
  } else {
    t51 = $[88];
  }
  let t52;
  if ($[89] !== segment || $[90] !== t50 || $[91] !== t51) {
    t52 = <div className="z-10 w-full bg-background"><SegmentedControl value={segment} onValueChange={t50} variant="ghost">{t51}</SegmentedControl></div>;
    $[89] = segment;
    $[90] = t50;
    $[91] = t51;
    $[92] = t52;
  } else {
    t52 = $[92];
  }
  let t53;
  if ($[93] !== table) {
    t53 = table.getHeaderGroups().map(_temp15);
    $[93] = table;
    $[94] = t53;
  } else {
    t53 = $[94];
  }
  let t54;
  if ($[95] !== t53) {
    t54 = <TableHeader>{t53}</TableHeader>;
    $[95] = t53;
    $[96] = t54;
  } else {
    t54 = $[96];
  }
  let t55;
  if ($[97] !== onSentenceTrack || $[98] !== sentences || $[99] !== table) {
    let t56;
    if ($[101] !== onSentenceTrack || $[102] !== sentences) {
      t56 = (row_5, index) => <TableRow key={row_5.id} row={row_5} root={rootRef} index={index + 1}><ExampleSentence sentences={sentences} wordOccurrences={row_5.original.wordOccurrences} onSentenceTrack={onSentenceTrack} /></TableRow>;
      $[101] = onSentenceTrack;
      $[102] = sentences;
      $[103] = t56;
    } else {
      t56 = $[103];
    }
    t55 = table.getRowModel().rows.map(t56);
    $[97] = onSentenceTrack;
    $[98] = sentences;
    $[99] = table;
    $[100] = t55;
  } else {
    t55 = $[100];
  }
  let t56;
  if ($[104] !== t55) {
    t56 = <tbody>{t55}</tbody>;
    $[104] = t55;
    $[105] = t56;
  } else {
    t56 = $[105];
  }
  let t57;
  if ($[106] !== t54 || $[107] !== t56) {
    t57 = <table className="relative min-w-full border-separate border-spacing-0">{t54}{t56}</table>;
    $[106] = t54;
    $[107] = t56;
    $[108] = t57;
  } else {
    t57 = $[108];
  }
  let t58;
  if ($[109] !== table) {
    t58 = <TableGoToLastPage table={table} />;
    $[109] = table;
    $[110] = t58;
  } else {
    t58 = $[110];
  }
  let t59;
  if ($[111] !== t57 || $[112] !== t58) {
    t59 = <div ref={rootRef} className="z-1 w-full grow overflow-auto overflow-y-scroll overscroll-contain [scrollbar-width:thin]">{t57}{t58}</div>;
    $[111] = t57;
    $[112] = t58;
    $[113] = t59;
  } else {
    t59 = $[113];
  }
  let t60;
  if ($[114] !== items || $[115] !== table) {
    t60 = <TablePagination items={items} table={table} rootRef={rootRef} />;
    $[114] = items;
    $[115] = table;
    $[116] = t60;
  } else {
    t60 = $[116];
  }
  let t61;
  if ($[117] !== table || $[118] !== tableState.pagination.pageSize) {
    t61 = <TablePaginationSizeSelect table={table} sizes={PAGE_SIZES} value={tableState.pagination.pageSize} />;
    $[117] = table;
    $[118] = tableState.pagination.pageSize;
    $[119] = t61;
  } else {
    t61 = $[119];
  }
  const t62 = `/${t("page")}`;
  let t63;
  if ($[120] !== t62) {
    t63 = <div className="px-1 whitespace-nowrap">{t62}</div>;
    $[120] = t62;
    $[121] = t63;
  } else {
    t63 = $[121];
  }
  let t64;
  if ($[122] !== t61 || $[123] !== t63) {
    t64 = <div className="flex grow items-center justify-end"><div className="flex items-center text-xs">{t61}{t63}</div></div>;
    $[122] = t61;
    $[123] = t63;
    $[124] = t64;
  } else {
    t64 = $[124];
  }
  let t65;
  if ($[125] !== t60 || $[126] !== t64) {
    t65 = <div className="flex w-full flex-wrap items-center justify-between gap-0.5 border-t border-t-zinc-200 py-1 pr-0.5 tracking-[.03em] tabular-nums dark:border-neutral-800">{t60}{t64}</div>;
    $[125] = t60;
    $[126] = t64;
    $[127] = t65;
  } else {
    t65 = $[127];
  }
  const t66 = ` ${t("vocabulary")}`;
  let t67;
  if ($[128] !== filteredRows.length || $[129] !== rowsAcquainted.length || $[130] !== rowsNew.length || $[131] !== t66) {
    t67 = <div className="flex w-full justify-center border-t border-solid border-t-zinc-200 bg-background dark:border-neutral-800"><VocabStatics total={filteredRows.length} text={t66} remaining={rowsNew.length} completed={rowsAcquainted.length} progress={true} /></div>;
    $[128] = filteredRows.length;
    $[129] = rowsAcquainted.length;
    $[130] = rowsNew.length;
    $[131] = t66;
    $[132] = t67;
  } else {
    t67 = $[132];
  }
  let t68;
  if ($[133] !== t26 || $[134] !== t48 || $[135] !== t52 || $[136] !== t59 || $[137] !== t65 || $[138] !== t67) {
    t68 = <div className={t26}>{t48}{t49}{t52}{t59}{t65}{t67}</div>;
    $[133] = t26;
    $[134] = t48;
    $[135] = t52;
    $[136] = t59;
    $[137] = t65;
    $[138] = t67;
    $[139] = t68;
  } else {
    t68 = $[139];
  }
  return t68;
}
function _temp15(headerGroup) {
  return <tr key={headerGroup.id}>{headerGroup.headers.map(_temp14)}</tr>;
}
function _temp14(header) {
  return <TableHeaderCellRender key={header.id} header={header} />;
}
function _temp13(segment_0) {
  return <SegmentItem key={segment_0.value} segment={segment_0} />;
}
function _temp12(e) {
  return e.preventDefault();
}
function _temp11(d_1) {
  return d_1.wordFamily.map(_temp10);
}
function _temp10(w) {
  return w.pathe;
}
function _temp9(d_0) {
  return !d_0.trackedWord.isBaseForm && !d_0.trackedWord.isUser && !d_0.trackedWord.rank;
}
function _temp8(d) {
  return d.trackedWord.learningPhase === LEARNING_PHASE.NEW && !d.trackedWord.rank;
}
function _temp7(row_4) {
  return row_4.original.trackedWord;
}
function _temp6(row_3) {
  switch (row_3.original.category) {
    case "properName":
      {
        return "properName";
      }
    case "acronym":
      {
        return "acronym";
      }
    default:
      {
        return "others";
      }
  }
}
function _temp5(row_1) {
  switch (row_1.original.trackedWord.learningPhase) {
    case LEARNING_PHASE.ACQUAINTED:
      {
        return "rowsAcquainted";
      }
    case LEARNING_PHASE.NEW:
      {
        return "rowsNew";
      }
    default:
      {
        return "_";
      }
  }
}
function _temp4(row) {
  return row.trackedWord.form;
}
function _temp3(error) {
  toast.error(error.message, {
    duration: Infinity
  });
}